import React, { useCallback, useState } from 'react';
import { useQuery } from 'react-query';
import { fetcher } from 'api/client';
import { getAuditTrail } from 'api/actions/contracts';
import { convertToBase64, digestMessageHex, parseDate, truncate } from 'utils';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Button, Container, Form, Jumbotron, Table } from 'reactstrap';
import { toast } from 'react-toastify';
import DragZone from 'components/Shared/DragZone';

const seguridataUrl = 'https://validador.seguridata.com:8443/ValidacionNOM/sgdata/home#hash';

function Hash({ data = {}, goToSeguridata, setData }) {

    const { t } = useTranslation();

    const copyToClipboard = (hash) => {
        navigator.clipboard.writeText(hash);
        toast.success(`${t('common.copied_clipboard')}: SHA-256: ${truncate(hash, 30)}`);
    };

    const handleClick = () => {
        goToSeguridata()
    }

    const handleReset = () => {
        setData({});
    }

    if (!data.hashv1) {
        return null;
    }

    return (
        <div className="mt-4">
            <h4 className="font-weight-bold my-4">
                {t('Documento')}:
            </h4>
            <h5>
                {data.name}
            </h5>
            <h4 className="font-weight-bold my-4">
                SHA-256:
            </h4>
            <div className="btn btn-outline-secondary btn-block pt-3" onClick={() => copyToClipboard(data.hashv1)}>
                <h5 className="pointer">
                    <b>v1:</b> {data.hashv1} <i className="fas fa-clipboard"></i>
                </h5>
            </div>
            <div className="btn btn-outline-secondary btn-block pt-3 mb-3 mt-3" onClick={() => copyToClipboard(data.hashv2)}>
                <h5 className="pointer">
                    <b>v2:</b> {data.hashv2} <i className="fas fa-clipboard"></i>
                </h5>
            </div>
            <div className="mt-5">

                <div>
                    <p className="text-muted text-left">
                        <small>
                            <b>v1:</b> {t('Documentos con Nom 151 generada previo a Agosto 2020')}
                    </small>
                        <br></br>
                        <small>
                            <b>v2:</b> {t('Documentos con Nom 151 generada posterior a Agosto 2020')}
                    </small>
                    </p>
                </div>
                <Button color="secondary" outline block onClick={handleClick} className="">
                    {t('Validar en Seguridata')}
                </Button>

                <Button color="secondary" outline block onClick={handleReset} className="mt-3 mb-4">
                    {t('Cargar nuevo documento')}
                </Button>
            </div>
        </div>
    )
}

function NomVerify() {

    const { t } = useTranslation();

    const [data, setData] = useState({});;

    const onDrop = async (files) => {
        const { base64, name } = await convertToBase64(files[0], true); // buffer from file
        const sha256hex = await digestMessageHex(base64); // sha-256 hex digest
        const sha256base64 = Buffer.from(sha256hex, 'hex'); // buffer from sha-256 hex digest
        const result = await digestMessageHex(sha256base64); // sha-256 hex digest
        setData({
            name,
            hashv1: String(result).toUpperCase(),
            hashv2: String(sha256hex).toUpperCase()
        });
    }

    const goToSeguridata = useCallback(() => {
        window.open(seguridataUrl, '_blank');
    }, [])

    return (<>
        <Jumbotron>
            <h1>{t('Verificación de Nom')}</h1>
            <div>
                <span>{t('Genera el hash de tu documento para su posterior validación en Seguridata')}</span>
            </div>
            <div className="mt-3">
                <small><a href={seguridataUrl} >{seguridataUrl}</a></small>
            </div>
        </Jumbotron>

        <Form>
            <div >
                {!data?.hashv1 &&
                    <DragZone
                        multiple={false}
                        imgSrc="/images/listing/createDocument/subir.svg"
                        label={"Cargar documento"}
                        className="frontdesk-drag bg-indigo-10"
                        acceptedFiles={["application/pdf"]}
                        noClick={() => { }}
                        handleFile={onDrop}
                        onDrop={onDrop}
                    />
                }
                <Hash
                    data={data}
                    goToSeguridata={goToSeguridata}
                    setData={setData}
                />
            </div>
        </Form>
    </>)

}

function AuditInformation({ contractId, jwt }) {

    const { t, i18n } = useTranslation();

    const { data: contract = {} } = useQuery('get-audit-trail', () => fetcher(getAuditTrail(contractId), jwt), {
        enabled: !!contractId,
        onSuccess: (data) => {
            if (data.language) i18n.changeLanguage(data.language.toLowerCase())
        }
    });
    return (
        <>
            <Jumbotron>
                <h1>{t('audit.header')}</h1>
                {t('audit.document_name')}: <b>{contract.name}</b>
            </Jumbotron>

            <Table hover>
                <tbody>
                    <tr>
                        <td><b>{t('audit.created_by')}:</b></td>
                        <td>
                            {contract.user?.givenName} {contract.user?.familyName}
                        </td>
                    </tr>
                    <tr>
                        <td><b>{t('audit.contractId')}:</b></td>
                        <td>{contract._id}</td>
                    </tr>
                    <tr>
                        <td><b>{t('audit.status')}:</b></td>
                        <td>{t('audit.signed_completed')} <i className="fas text-success fa-check"></i></td>
                    </tr>
                    <tr>
                        <td><b>{t('audit.signature_type')}:</b></td>
                        <td>{t('audit.signature')}</td>
                    </tr>
                    <tr>
                        <td><b>{t('audit.signature_date')}:</b></td>
                        <td>{parseDate(contract.signedAt, 'Pp')}</td>
                    </tr>
                    <tr>
                        <td><b>{t('audit.signers')}:</b></td>
                        <td>
                            {contract.participants?.map((p) => (
                                <ul key={p._id}>
                                    <li><b>{t('audit.name')}:</b> <span>{p.name}</span></li>
                                    {p.obligation === 'individual' ?
                                        <li><b>{t('audit.last_name')}:</b> <span>{p.last_name}</span></li> :
                                        ''}
                                    <li><b>{t('audit.email')}:</b> <span>{p.email}</span></li>
                                    <li><b>{t('audit.hash')}:</b> <span>{p.hash}</span></li>
                                    <li><b>IP:</b> <span>{p.ip}</span></li>
                                    <li><b>{t('audit.signed')}:</b> <span>{parseDate(p.signedAt, 'Pp')}</span></li>
                                </ul>
                            ))
                            }
                        </td>
                    </tr>
                </tbody>
            </Table>
        </>)
}

export default function Audit() {
    const { contractId, jwt } = useParams();

    return (
        <Container>
            <nav className="navbar navbar-inverse navbar-fixed-top">
                <div className="navbar-header">
                    <a className="navbar-brand" href="https://trato.io">
                        <img src="/images/logo-blanco.svg" alt="logo" />
                    </a>
                </div>
            </nav>
            {contractId ?
                <AuditInformation contractId={contractId} jwt={jwt} /> :
                <NomVerify />
            }
            <hr />
            <div className="text-center">
                <img src="/images/logo-blanco.svg" alt="logo" width="200px" className="m-3" />
                <p>
                    <b>© 2014-2021 ContratosApp, S.A.P.I. de C.V.</b>
                </p>
            </div>
        </Container>
    )

}